import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, X, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/api";
import { isPushSupported, getSubscriptionStatus, subscribePush, unsubscribePush, sendPushTest, ensureServiceWorker } from "@/pushClient";

const ICON = "/pag-icon-192.png";

const DISMISS_KEY = "pag_push_prompt_dismissed_at";
const DISMISS_HOURS = 24 * 7; // Rebrancher le prompt tous les 7j si dismiss

function shouldShowPrompt() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return true;
  const t = Number(raw);
  if (!t) return true;
  return (Date.now() - t) / 36e5 > DISMISS_HOURS;
}

/**
 * Prompt qui apparaît en haut de page pour proposer d'activer les notifications.
 * Se masque si déjà autorisé, refusé ou dismiss récemment.
 */
export function PushEnableBanner() {
  const [status, setStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isPushSupported()) {
      setStatus({ supported: false, permission: "unsupported", subscribed: false });
      return;
    }
    try {
      await ensureServiceWorker();
    } catch (_) {}
    const s = await getSubscriptionStatus();
    setStatus(s);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!status.supported) return setVisible(false);
    // On propose seulement si non abonné + non refusé + non dismiss récent
    setVisible(!status.subscribed && status.permission !== "denied" && shouldShowPrompt());
  }, [status]);

  const enable = async () => {
    setLoading(true);
    try {
      await subscribePush();
      toast.success("Notifications activées ✔");
      localStorage.removeItem(DISMISS_KEY);
      await refresh();
      setVisible(false);
    } catch (e) {
      toast.error(e.message || "Impossible d'activer les notifications");
    } finally {
      setLoading(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div data-testid="push-banner" className="bg-[#002FA7] text-white px-4 py-3 flex items-center gap-3 shadow-lg">
      <BellRing className="h-5 w-5 flex-shrink-0 animate-pulse" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm">Activez les notifications instantanées</div>
        <div className="text-xs opacity-90">Recevez les messages, congés et tickets même quand PAG est fermé — comme un SMS.</div>
      </div>
      <Button
        onClick={enable}
        disabled={loading}
        data-testid="push-enable-btn"
        className="bg-white text-[#002FA7] hover:bg-[#F1F3F5] font-bold text-xs h-8 uppercase tracking-[0.15em]"
      >
        {loading ? "Activation…" : "Activer"}
      </Button>
      <button
        type="button"
        onClick={dismiss}
        data-testid="push-dismiss-btn"
        className="p-1 hover:bg-white/10 rounded"
        aria-label="Fermer"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Toggle complet + bouton test à mettre dans une page paramètres/profil.
 */
export function PushSettingsToggle() {
  const [status, setStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isPushSupported()) return setStatus({ supported: false, permission: "unsupported", subscribed: false });
    await ensureServiceWorker();
    setStatus(await getSubscriptionStatus());
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = async () => {
    setLoading(true);
    try {
      await subscribePush();
      toast.success("Notifications activées ✔");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally { setLoading(false); }
  };
  const disable = async () => {
    setLoading(true);
    try {
      await unsubscribePush();
      toast.success("Notifications désactivées");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally { setLoading(false); }
  };
  const test = async () => {
    try {
      const r = await sendPushTest({});
      if (r.sent > 0) toast.success(`Notification test envoyée sur ${r.sent} appareil${r.sent > 1 ? "s" : ""}`);
      else if (r.disabled) toast.error("Web push non configuré côté serveur");
      else toast.warning("Aucun appareil abonné trouvé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi test");
    }
  };

  if (!status.supported) {
    return (
      <div className="border border-[#DEE2E6] bg-white p-4 flex items-center gap-3" data-testid="push-toggle-unsupported">
        <BellOff className="h-5 w-5 text-[#868E96]" />
        <div className="text-sm text-[#868E96]">Votre navigateur ne supporte pas les notifications push.</div>
      </div>
    );
  }

  return (
    <div className="border border-[#DEE2E6] bg-white p-4" data-testid="push-toggle">
      <div className="flex items-start gap-3">
        {status.subscribed ? <Bell className="h-5 w-5 text-emerald-600 mt-0.5" /> : <BellOff className="h-5 w-5 text-[#868E96] mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Notifications instantanées</div>
          <div className="text-xs text-[#495057] mt-1">
            {status.permission === "denied"
              ? "Vous avez refusé les notifications. Autorisez PAG dans les paramètres du navigateur pour les réactiver."
              : status.subscribed
                ? "Vous êtes abonné(e). Vous recevrez les messages et alertes même si le site est fermé."
                : "Activez pour recevoir messages, tickets et alertes comme un SMS, sur PC et mobile."}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {!status.subscribed && status.permission !== "denied" && (
              <Button onClick={enable} disabled={loading} data-testid="push-toggle-enable" className="bg-[#002FA7] hover:bg-[#0A0A0A] text-white h-8 text-xs uppercase tracking-[0.15em]">
                {loading ? "…" : "Activer"}
              </Button>
            )}
            {status.subscribed && (
              <>
                <Button onClick={test} variant="outline" data-testid="push-toggle-test" className="h-8 text-xs uppercase tracking-[0.15em]">
                  Envoyer un test
                </Button>
                <Button onClick={disable} disabled={loading} variant="outline" data-testid="push-toggle-disable" className="h-8 text-xs uppercase tracking-[0.15em] text-red-600 border-red-200 hover:bg-red-50">
                  Désactiver
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Snooze / Quiet hours settings — bloque les push non-urgents pendant les heures calmes.
 */
export function SnoozeSettings() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/notifications/prefs").then((r) => setPrefs(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/notifications/prefs", {
        quiet_hours: prefs.quiet_hours,
        min_priority_during_quiet: prefs.min_priority_during_quiet,
        blocked_categories: prefs.blocked_categories,
      });
      toast.success("Préférences enregistrées");
      setPrefs({
        quiet_hours: data.quiet_hours,
        min_priority_during_quiet: data.min_priority_during_quiet,
        blocked_categories: data.blocked_categories,
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!prefs) return <div className="border border-[#DEE2E6] bg-white p-4 text-sm text-[#868E96]">Chargement…</div>;

  return (
    <div className="border border-[#DEE2E6] bg-white p-4 space-y-4" data-testid="snooze-settings">
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Moon className="h-4 w-4 text-[#002FA7]" />
            <div>
              <div className="font-bold text-sm">Heures calmes (Ne pas déranger)</div>
              <div className="text-xs text-[#495057]">Silence les notifications non-urgentes pendant vos horaires nocturnes.</div>
            </div>
          </div>
          <Switch
            checked={prefs.quiet_hours?.enabled || false}
            onCheckedChange={(v) => setPrefs({ ...prefs, quiet_hours: { ...prefs.quiet_hours, enabled: v } })}
            data-testid="snooze-quiet-toggle"
          />
        </div>
        {prefs.quiet_hours?.enabled && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Début (HH:MM)</Label>
              <Input
                type="time"
                value={prefs.quiet_hours.start || "22:00"}
                onChange={(e) => setPrefs({ ...prefs, quiet_hours: { ...prefs.quiet_hours, start: e.target.value } })}
                data-testid="snooze-start"
              />
            </div>
            <div>
              <Label className="text-xs">Fin (HH:MM)</Label>
              <Input
                type="time"
                value={prefs.quiet_hours.end || "07:30"}
                onChange={(e) => setPrefs({ ...prefs, quiet_hours: { ...prefs.quiet_hours, end: e.target.value } })}
                data-testid="snooze-end"
              />
            </div>
          </div>
        )}
      </div>

      <div>
        <Label className="text-xs">Priorité minimum autorisée pendant les heures calmes</Label>
        <Select value={prefs.min_priority_during_quiet || "high"} onValueChange={(v) => setPrefs({ ...prefs, min_priority_during_quiet: v })}>
          <SelectTrigger data-testid="snooze-min-prio"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Toutes (aucun blocage)</SelectItem>
            <SelectItem value="medium">Moyenne ou plus</SelectItem>
            <SelectItem value="high">Haute ou plus (recommandé)</SelectItem>
            <SelectItem value="urgent">Urgente uniquement</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-[10px] text-[#868E96] mt-1">Les notifications en-dessous de ce niveau sont silencées pendant les heures calmes.</div>
      </div>

      <div>
        <Label className="text-xs">Catégories bloquées (toujours)</Label>
        <div className="flex gap-2 flex-wrap mt-2">
          {["general", "ticket", "ticket_comment", "photo", "intervention"].map((cat) => {
            const active = (prefs.blocked_categories || []).includes(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => {
                  const next = active ? prefs.blocked_categories.filter((c) => c !== cat) : [...(prefs.blocked_categories || []), cat];
                  setPrefs({ ...prefs, blocked_categories: next });
                }}
                data-testid={`snooze-cat-${cat}`}
                className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 border font-semibold transition-colors ${active ? "bg-red-100 border-red-300 text-red-700" : "bg-white border-[#DEE2E6] text-[#495057] hover:bg-[#F1F3F5]"}`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="w-full bg-[#002FA7] hover:bg-[#0A0A0A]" data-testid="snooze-save">
        {saving ? "Enregistrement…" : "Enregistrer les préférences"}
      </Button>
    </div>
  );
}
